<template>
  <v-col class="calendar-informs-container">
    <v-row>
      <div class="inform-picker">
        <v-select
          v-model="selectedInform"
          :items="informsList"
          item-text="label"
          item-value="id"
          :label="informTypeText"
          color="secondary"
          item-color="secondary"
          hide-details
        ></v-select>
      </div>
      <div class="date-picker">
        <v-menu
          v-model="fromDatePicker"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              ref="fromDateInput"
              v-model="fromDateInput"
              :label="fromDateText"
              color="secondary"
              prepend-icon="mdi-calendar"
              :error-messages="fromDateErrors"
              v-bind="attrs"
              v-on="on"
              hide-details="auto"
              @click:prepend="openFromDatePicker"
              @blur="commitFromDate(false)"
              @keydown.enter="commitFromDate(true)"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="fromDate"
            no-title
            scrollable
            :locale="locale"
            :max="toDate"
            @input="setFromDate"
          />
        </v-menu>
      </div>
      <div class="date-picker">
        <v-menu
          v-model="toDatePicker"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              ref="toDateInput"
              v-model="toDateInput"
              :label="toDateText"
              color="secondary"
              prepend-icon="mdi-calendar"
              :error-messages="toDateErrors"
              v-bind="attrs"
              v-on="on"
              hide-details="auto"
              @click:prepend="openToDatePicker"
              @blur="commitToDate(false)"
              @keydown.enter="commitToDate(true)"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="toDate"
            no-title
            scrollable
            :locale="locale"
            :min="fromDate"
            @input="setToDate"
          />
        </v-menu>
      </div>
      <v-spacer></v-spacer>
      <v-text-field
        class="search-field"
        v-model="search"
        append-icon="mdi-magnify"
        :label="searchText"
        single-line
        hide-details
        color="secondary"
      ></v-text-field>
    </v-row>
    <v-row justify="start">
      <v-btn
        small
        color="accent"
        class="ma-2"
        @click="exportExcel"
        :disabled="!currentInformItems.length"
      >
        <v-icon left dark>
          mdi-file-excel
        </v-icon>
        {{ downloadInformText }}
      </v-btn>
    </v-row>
    <v-row>
      <v-col>
        <v-data-table
          :headers="selectedInform === 1 ? headers : headersCount"
          :items="selectedInform === 1 ? informEvents : informCount"
          :items-per-page="-1"
          item-key="id"
          :footer-props="footerProps"
          :search="search"
          class="elevation-1"
          :no-data-text="noEventsText"
          :no-results-text="noResultsText"
          :mobile-breakpoint="0"
          :sort-by="selectedInform === 1 ? 'start' : ['count', 'leader']"
          :sort-desc="selectedInform === 1 ? false : true"
          @current-items="setCurrentInformItems"
          multi-sort
        >
          <template v-slot:item.name="{ item }">
            <TextHighlight :queries="search">{{ item.name }}</TextHighlight>
          </template>
          <template v-slot:item.start="{ item }">
            <TextHighlight :queries="search">{{ item.start }}</TextHighlight>
          </template>
          <template v-slot:item.end="{ item }">
            <TextHighlight :queries="search">{{ item.end }}</TextHighlight>
          </template>
          <template v-slot:item.leadersText="{ item }">
            <TextHighlight :queries="search">{{
              item.leadersText
            }}</TextHighlight>
          </template>
          <template v-slot:item.leader="{ item }">
            <TextHighlight :queries="search">{{ item.leader }}</TextHighlight>
          </template>
          <template v-slot:item.email="{ item }">
            <TextHighlight :queries="search">{{ item.email }}</TextHighlight>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </v-col>
</template>

<script>
import TextHighlight from 'vue-text-highlight'
import { mapState } from 'vuex'
import { getCalendarDate, getCalendarDateFirstDayOfMonth } from '@/utils/common'
import {
  getEventsFromDates,
  getEventsPerLeader
} from '@/utils/calendar-informs'
import XLSX from 'xlsx'
import { saveAs } from 'file-saver'

export default {
  components: {
    TextHighlight
  },
  data: () => ({
    fromDatePicker: false,
    toDatePicker: false,
    fromDate: getCalendarDateFirstDayOfMonth(),
    toDate: getCalendarDate(new Date().getTime()),
    fromDateInput: getCalendarDateFirstDayOfMonth(),
    toDateInput: getCalendarDate(new Date().getTime()),
    selectedInform: 1,
    search: '',
    informEvents: [],
    informCount: [],
    currentInformItems: [],
    locale: localStorage.getItem('bd-kol-lang') || 'es'
  }),
  created() {
    this.loadInform()
  },
  computed: {
    ...mapState({
      leaders: state => state.leaderStore.leaders,
      leadersMap: state => state.leaderStore.leadersMap,
      events: state => state.eventStore.events
    }),
    footerProps() {
      return {
        itemsPerPageText: this.$t('admin.itemsPerPage'),
        itemsPerPageAllText: this.$t('admin.all'),
        itemsPerPageOptions: [10, 20, 50, 100, -1]
      }
    },
    noResultsText() {
      return this.$t('admin.noResultsText')
    },
    nameText() {
      return this.$t('calendar.name')
    },
    descriptionText() {
      return this.$t('calendar.description')
    },
    assoLeadersText() {
      return this.$t('calendar.assoLeaders')
    },
    startText() {
      return this.$t('calendar.start')
    },
    endText() {
      return this.$t('calendar.end')
    },
    eventsQuantityText() {
      return this.$t('inform.eventsQuantity')
    },
    leaderText() {
      return this.$t('calendar.leader')
    },
    emailText() {
      return this.$t('fields.email')
    },
    informTypeText() {
      return this.$t('inform.type')
    },
    fromDateText() {
      return this.$t('inform.fromDate')
    },
    toDateText() {
      return this.$t('inform.toDate')
    },
    downloadInformText() {
      return this.$t('inform.downloadInform')
    },
    invalidDateText() {
      return this.$t('inform.invalidDate')
    },
    fromDateErrors() {
      return this.isValidDate(this.fromDateInput) ? [] : [this.invalidDateText]
    },
    toDateErrors() {
      return this.isValidDate(this.toDateInput) ? [] : [this.invalidDateText]
    },
    searchText() {
      return this.$t('common.search')
    },
    noEventsText() {
      return this.$t('inform.noEvents')
    },
    informsList() {
      return [
        { id: 1, label: this.$t('inform.eventsList') },
        { id: 2, label: this.$t('inform.eventsPerLeader') }
      ]
    },
    headers() {
      return [
        { text: this.nameText, value: 'name', width: '200px' },
        { text: this.startText, value: 'start', width: '150px' },
        { text: this.endText, value: 'end', width: '150px' },
        { text: this.assoLeadersText, value: 'leadersText', width: '300px' },
        { text: this.descriptionText, value: 'description', width: '250px' }
      ]
    },
    headersCount() {
      return [
        { text: this.eventsQuantityText, value: 'count', width: '185px' },
        { text: this.leaderText, value: 'leader', width: '300px' },
        { text: this.emailText, value: 'email', width: '200px' }
      ]
    },
    activeHeaders() {
      return this.selectedInform === 1 ? this.headers : this.headersCount
    },
    activeInformItems() {
      return this.selectedInform === 1 ? this.informEvents : this.informCount
    },
    selectedInformLabel() {
      const inform = this.informsList.find(
        item => item.id === this.selectedInform
      )
      return inform ? inform.label : ''
    }
  },
  watch: {
    events() {
      this.loadInform()
    },
    selectedInform() {
      this.setCurrentInformItems(this.activeInformItems)
    }
  },
  methods: {
    focusDateInput(ref) {
      this.$nextTick(() => {
        if (this.$refs[ref]) {
          this.$refs[ref].focus()
        }
      })
    },
    openFromDatePicker() {
      this.fromDatePicker = true
      this.focusDateInput('fromDateInput')
    },
    openToDatePicker() {
      this.toDatePicker = true
      this.focusDateInput('toDateInput')
    },
    parseDate(date) {
      const value = String(date || '').trim()
      const calendarDate = value.match(/^(\d{4})-(\d{2})-(\d{2})$/)
      const visibleDate = value.match(/^(\d{2})-(\d{2})-(\d{4})$/)

      if (calendarDate) {
        return this.getValidDate(
          calendarDate[1],
          calendarDate[2],
          calendarDate[3]
        )
      }

      if (visibleDate) {
        return this.getValidDate(visibleDate[3], visibleDate[2], visibleDate[1])
      }

      return ''
    },
    getValidDate(year, month, day) {
      const date = new Date(`${year}-${month}-${day} 00:00`)
      if (
        date.getFullYear() !== Number(year) ||
        date.getMonth() + 1 !== Number(month) ||
        date.getDate() !== Number(day)
      ) {
        return ''
      }
      return `${year}-${month}-${day}`
    },
    isValidDate(date) {
      return Boolean(this.parseDate(date))
    },
    commitFromDate(closePicker) {
      const date = this.parseDate(this.fromDateInput)
      if (!date) return
      this.setFromDate(date, closePicker)
    },
    commitToDate(closePicker) {
      const date = this.parseDate(this.toDateInput)
      if (!date) return
      this.setToDate(date, closePicker)
    },
    setFromDate(date, closePicker = true) {
      this.fromDate = date
      if (this.toDate < this.fromDate) {
        this.toDate = this.fromDate
      }
      this.fromDateInput = this.fromDate
      this.toDateInput = this.toDate
      if (closePicker) {
        this.fromDatePicker = false
      }
      this.loadInform()
    },
    setToDate(date, closePicker = true) {
      this.toDate = date
      if (this.fromDate > this.toDate) {
        this.fromDate = this.toDate
      }
      this.fromDateInput = this.fromDate
      this.toDateInput = this.toDate
      if (closePicker) {
        this.toDatePicker = false
      }
      this.loadInform()
    },
    setCurrentInformItems(items) {
      if (this.sameInformItems(items, this.currentInformItems)) return
      this.currentInformItems = items
    },
    sameInformItems(itemsA, itemsB) {
      if (itemsA.length !== itemsB.length) return false
      return itemsA.every((item, index) => item.id === itemsB[index].id)
    },
    getExportableInformData(items) {
      return items.map(item =>
        this.activeHeaders.reduce(
          (acc, header) => ({
            ...acc,
            [header.text]: item[header.value]
          }),
          {}
        )
      )
    },
    getSheetColumnWidths() {
      return this.selectedInform === 1
        ? [200, 100, 100, 250, 500].map(width => ({ wpx: width }))
        : [106, 250, 250].map(width => ({ wpx: width }))
    },
    getSheetName() {
      return this.selectedInformLabel.substring(0, 31)
    },
    getExcelBuffer(data) {
      const sheetName = this.getSheetName()
      const worksheet = XLSX.utils.json_to_sheet(data)
      const workbook = XLSX.utils.book_new()
      const columnWidths = this.getSheetColumnWidths()

      if (columnWidths.length) {
        worksheet['!cols'] = columnWidths
      }

      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName)
      const content = XLSX.write(workbook, {
        bookType: 'xlsx',
        bookSST: true,
        type: 'binary'
      })
      const buffer = new ArrayBuffer(content.length)
      const view = new Uint8Array(buffer)

      for (let i = 0; i !== content.length; i++) {
        view[i] = content.charCodeAt(i) & 0xff
      }

      return buffer
    },
    exportExcel() {
      try {
        const fileName =
          this.selectedInformLabel +
          ' - ' +
          new Date().toDateString().substring(3)
        const data = this.getExportableInformData(this.currentInformItems)
        const buffer = this.getExcelBuffer(data)
        const blob = new Blob([buffer], {
          type: 'application/octet-stream'
        })

        saveAs(blob, fileName + '.xlsx')
      } catch (e) {
        return e
      }
    },
    loadInform() {
      this.informEvents = getEventsFromDates(
        this.fromDate,
        this.toDate,
        this.events
      )
      this.informCount = getEventsPerLeader(
        this.fromDate,
        this.toDate,
        this.events,
        this.leadersMap
      )
      this.setCurrentInformItems(this.activeInformItems)
    }
  }
}
</script>
<style lang="scss">
.calendar-informs-container {
  word-break: break-word;

  .search-field {
    margin-top: 16px;
    margin-right: 12px;
    max-width: 350px;
  }
  .inform-picker {
    width: 190px;
    margin: 12px;
  }
  .date-picker {
    width: 220px;
    margin: 12px;
  }

  @media (max-width: 599px) {
    .inform-picker,
    .date-picker {
      width: 100%;
    }
  }

  .text__highlight {
    background-color: #adeeff;
  }
}
</style>
